import { LiaElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { request } from '../../shared/api/http.js';
import { currentParams } from '../../shared/state/urlState.js';
import { LOGIN_PATH } from '../../shared/paths.js';
import {
   acceptInvite, clearDeveloper, getDeveloper, isDeveloperSignedIn, myAccounts, switchAccount
} from '../api/developer.js';

const PROVIDER_LABELS = { github: 'GitHub' };

/**
 * Where a magic link and a seat invitation land.
 *
 * The browser half of the device flow: the person clicks a link in their mail, types the code the client
 * showed them, and the client that started the flow - a CLI printing a code, an editor extension, the
 * sign-in page in another tab - is unblocked by its own poll. **Nothing is handed back here.** The link
 * proves control of an address; the session goes to the client that holds the device code, because
 * otherwise a forwarded mail would be a sign-in.
 *
 * **The code is asked for, not skipped.** The link alone proves that whoever opened it can read the
 * address's mail. It does not prove they asked for it: anybody can type somebody else's address into the
 * sign-in page, and the owner then receives a genuine link. The code is shown only where the flow was
 * started, so typing it here is what proves the person at the link is the person who asked. A wrong code
 * spends nothing and can be tried again.
 *
 * **An invitation belongs to an address, and an account is its one e-mail.** Only the account with the invited
 * address accepts. Signed in as any other, the service answers 409 `other_account` naming the address (the
 * invitation stays open), and the page offers the two ways forward: "Accept as <address>", where the
 * invitation mail is the proof of the address (a free address becomes a new account, and the seat is claimed; the
 * page names the provider account of this sign-in that it links to the new account, or says that none is linked
 * when the sign-in has none - a mailbox proves the mailbox, D32), and "Sign in to <address>". An address another
 * account already has cannot be accepted as: that account signs in itself.
 *
 * **An invitation needs a session.** A link never creates a person, so accepting one attaches the
 * invited address to whoever is signed in and hands them the seat. Signed in, the invitation is tried
 * first, because it needs no code, and the invitation endpoint refuses a sign-in token without spending
 * it. Not signed in, a link and an invitation look identical, so the page offers both: the code for a
 * sign-in, and a provider sign-in for an invitation - which brings the person straight back here to accept.
 */
export class UserCovdbgLogin extends LiaElement {
   static properties = {
      state: { state: true },
      detail: { state: true },
      error: { state: true },
      busy: { state: true },
      invited: { state: true },
      identity: { state: true },
      reason: { state: true }
   };

   constructor() {
      super();
      this.state = 'working';
      this.detail = null;
      this.error = '';
      this.busy = false;

      // The address an invitation is for, when the signed-in account is not it; and why it cannot be accepted as.
      this.invited = '';

      // The provider account the session signed in with, which "accept as" would link to the new account (null: none).
      this.identity = null;
      this.reason = '';
   }

   connectedCallback() {
      super.connectedCallback();
      void this.arrive();
   }

   get token() {
      return currentParams().get('token');
   }

   /** Where signing in returns to: this page, with the same link. */
   get signInHref() {
      return `${LOGIN_PATH}?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
   }

   async arrive() {
      const token = this.token;

      if (!token) {
         this.state = 'missing';
         return;
      }

      if (!isDeveloperSignedIn()) {
         // Nobody to accept an invitation as. The code form, with the way to sign in beside it.
         this.state = 'code';
         return;
      }

      try {
         this.detail = await acceptInvite(token);
         this.state = 'accepted';
      } catch (error) {
         if (error.status === 409 && error.details?.title === 'other_account') {
            this.invited = error.details.invitedEmail ?? '';
            this.identity = error.details.identity ?? null;
            this.state = 'other';
            return;
         }

         // Not an invitation, so a sign-in link: those need the code before anything happens.
         this.state = 'code';
      }
   }

   /** The invitation mail is the proof of the address: the invited account is made and this sign-in linked to it. */
   async acceptAsInvited() {
      this.busy = true;
      this.reason = '';

      try {
         this.detail = await acceptInvite(this.token, { asInvitedAddress: true });
         this.state = 'acceptedAs';
      } catch (error) {
         const title = error.details?.title;

         if (error.status === 409 && title === 'email_taken') {
            this.reason = title;
         } else {
            this.reason = 'failed';
            this.detail = { message: error.message };
         }
      } finally {
         this.busy = false;
      }
   }

   /**
    * Signs in to the invited address: a switch when this sign-in already reaches that account, otherwise a
    * sign-out here and the sign-in page, which brings the person back to this link.
    */
   async signInToInvited() {
      this.busy = true;

      try {
         const account = (await myAccounts()).find(entry => entry.email === this.invited.toLowerCase());

         if (account) {
            const { exchange } = await switchAccount(account.principalId);
            const back = `${location.pathname}${location.search}`;

            location.assign(`/login/exchange?token=${encodeURIComponent(exchange)}&next=${encodeURIComponent(back)}`);
            return;
         }
      } catch {
         // Fall through to a plain sign-in.
      }

      clearDeveloper();
      location.assign(this.signInHref);
   }

   async confirm(event) {
      event.preventDefault();

      const code = String(new FormData(event.target).get('code') ?? '').trim();

      if (!code) {
         return;
      }

      this.busy = true;
      this.error = '';

      try {
         this.detail = await request('/api/v1/seats/login/complete', {
            body: { token: this.token, code },
            auth: false
         });
         this.state = 'done';
      } catch (error) {
         if (error.details?.title === 'code_mismatch') {
            // The link is still open. Say so and let them read the code again.
            this.error = error.message;
         } else {
            this.detail = { message: error.message };
            this.state = 'failed';
         }
      } finally {
         this.busy = false;
      }
   }

   render() {
      return html`
         <lia-auth-layout brand="covdbg" heading="Confirm" .showThemeToggle=${false}>
            ${this.state === 'working'
               ? html`<p class="text-body-secondary"><lia-spinner></lia-spinner> One moment…</p>`
               : nothing}

            ${this.state === 'missing'
               ? html`
                  <lia-alert
                     variant="danger"
                     icon="fa-solid fa-triangle-exclamation"
                     message="This link carries no token. Open the one from your mail without editing it."
                  ></lia-alert>`
               : nothing}

            ${this.state === 'code' ? this.codeForm : nothing}

            ${this.state === 'code' && !isDeveloperSignedIn() ? this.invitation : nothing}

            ${this.state === 'other' ? this.otherAccount : nothing}

            ${this.state === 'accepted' || this.state === 'acceptedAs'
               ? html`
                  <lia-alert
                     variant="success"
                     icon="fa-solid fa-circle-check"
                     message=${this.state === 'acceptedAs'
                        ? `Accepted. The seat is held by the account ${this.detail?.primaryEmail ?? this.invited}, which is listed under Other accounts on your profile now.`
                        : this.detail?.primaryEmail
                           ? `Accepted. The seat is yours, as ${this.detail.primaryEmail}.`
                           : 'Accepted. The seat is yours.'}
                  ></lia-alert>
                  <a class="btn btn-primary btn-sm" href=${this.state === 'acceptedAs' ? '/profile' : '/'}>
                     ${this.state === 'acceptedAs' ? 'Go to your profile' : 'Go to covdbg'}
                  </a>`
               : nothing}

            ${this.state === 'failed'
               ? html`
                  <lia-alert
                     variant="danger"
                     icon="fa-solid fa-triangle-exclamation"
                     message=${this.detail?.message
                        ?? 'This link has expired or has already been used. Ask for a new one.'}
                  ></lia-alert>`
               : nothing}

            ${this.state === 'done'
               ? html`
                  <lia-alert
                     variant="success"
                     icon="fa-solid fa-circle-check"
                     message=${this.detail?.primaryEmail
                        ? `Confirmed as ${this.detail.primaryEmail}.`
                        : 'Confirmed.'}
                  ></lia-alert>
                  <p class="text-body-secondary">
                     You can close this tab. If you started this from the covdbg CLI or the editor
                     extension, it is already signed in.
                  </p>
                  <a class="btn btn-outline-secondary btn-sm" href="/">Go to covdbg</a>`
               : nothing}
         </lia-auth-layout>`;
   }

   /** The invitation is for another account than the one signed in. */
   get otherAccount() {
      const signedInAs = getDeveloper()?.email;
      const refusal = {
         email_taken: `An account already has ${this.invited}. Sign in to it to accept the invitation.`,
         failed: this.detail?.message ?? 'That did not work.'
      }[this.reason];
      const blocked = this.reason === 'email_taken';

      return html`
         <p data-invited-for>
            This invitation is for <strong>${this.invited}</strong>${signedInAs ? html`, and you are signed in as <strong>${signedInAs}</strong>` : nothing}.
            A seat belongs to the account with that address.
         </p>
         ${refusal
            ? html`<lia-alert variant="warning" icon="fa-solid fa-triangle-exclamation" message=${refusal} class="d-block mb-3"></lia-alert>`
            : nothing}
         <div class="d-grid gap-2">
            ${blocked
               ? nothing
               : html`
                  <button class="btn btn-primary" type="button" data-accept-as ?disabled=${this.busy} @click=${() => this.acceptAsInvited()}>
                     Accept as ${this.invited}
                  </button>
                  <p class="form-text mt-0 mb-1" data-accept-as-note>
                     ${this.identity
                        ? html`The invitation mail proves you own the address, so a new account for it is created.
                           This links your ${PROVIDER_LABELS[this.identity.provider] ?? this.identity.provider}
                           account <strong>${this.identity.subject}</strong> to <strong>${this.invited}</strong>.`
                        : html`The invitation mail proves you own the address, so a new account for it is created.
                           This sign-in has no provider account, so nothing is linked: you sign in to
                           ${this.invited} by a link mailed to it.`}
                  </p>`}
            <button class="btn btn-outline-secondary" type="button" data-sign-in-to ?disabled=${this.busy} @click=${() => this.signInToInvited()}>
               Sign in to ${this.invited}
            </button>
         </div>`;
   }

   /** The other thing this link may be, for somebody who holds no session to accept it with. */
   get invitation() {
      return html`
         <hr>
         <p class="text-body-secondary small mb-2">
            Accepting an invitation to a team? Sign in first - the invitation is accepted for whoever is
            signed in, and this page comes back to do that.
         </p>
         <a class="btn btn-outline-primary btn-sm" href=${this.signInHref}>
            <i class="fa-solid fa-right-to-bracket me-2" aria-hidden="true"></i>Sign in
         </a>`;
   }

   /** The one question this page asks: the code shown where the sign-in was started. */
   get codeForm() {
      return html`
         <form @submit=${event => this.confirm(event)}>
            <p class="text-body-secondary small">
               Enter the code shown where you asked to sign in - in the covdbg CLI, the editor extension,
               or the sign-in page you left open. If you did not ask for this, close this tab: nothing has
               happened.
            </p>
            ${this.error
               ? html`<lia-alert variant="warning" icon="fa-solid fa-triangle-exclamation" message=${this.error}></lia-alert>`
               : nothing}
            <div class="mb-3">
               <label class="form-label" for="login-code">Code</label>
               <input
                  id="login-code"
                  class="form-control form-control-lg font-monospace text-uppercase"
                  name="code"
                  autocomplete="one-time-code"
                  inputmode="text"
                  spellcheck="false"
                  placeholder="XXXX-XXXX"
                  maxlength="12"
                  required
                  autofocus
               />
            </div>
            <button class="btn btn-primary w-100" type="submit" ?disabled=${this.busy}>
               ${this.busy ? html`<lia-spinner size="sm" class="me-2"></lia-spinner>Confirming…` : 'Confirm'}
            </button>
         </form>`;
   }
}

customElements.define('user-covdbg-login', UserCovdbgLogin);
